import React, { useMemo } from 'react';
import { ActivityHistoryCard } from '@europrocurement/flexy-components';
import { UtilisateurTaches } from '@europrocurement/l2d-domain';
import { useTheme } from '@mui/material';
import { addNoteIcon, addTaskIcon, forbiddenIcon, preview } from '@europrocurement/l2d-icons';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { PrescripteurTachesApiObject } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import useTasksHook from '@europrocurement/l2d-modules/modules/Tasks/hook/useTasksHook';
import TasksCardContent from '@europrocurement/l2d-modules/modules/Tasks/components/TasksCardContent/TasksCardContent';
import ActivitiesCardContent from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/ActivitiesCardContent/ActivitiesCardContent';
import usePublisherActivitiesHook from '@europrocurement/l2d-modules/modules/Fournisseurs/hooks/usePublisherActivitiesHook';
import {
    MAX_NOTES_FETCH_ATTEMPTS,
    useCurrentPublisherCtx,
} from '@europrocurement/l2d-modules/modules/Fournisseurs/providers/CurrentPublisherProvider';

interface PublisherActivityTabProps {
    showTask?: boolean;
    showTechniqueLogs?: boolean;
    noteMemo?: boolean;
    showNotes?: boolean;
}

const PublisherActivityTab: React.FunctionComponent<PublisherActivityTabProps> = function ({
    showTask = true,
    showNotes = true,
    showTechniqueLogs = false,
    noteMemo = true,
}) {
    const { palette } = useTheme();
    const { publisher } = useCurrentPublisherCtx();
    const [showLogsTechniques, setShowLogsTechniques] = React.useState(false);

    const {
        activities,
        activitiesLoading,
        notesFetchFailed,
        notesFetchAttempt,
        isFetchingNotes,
        handleAddNote,
        handleEditNote,
        handleDeleteNote,
        handleRetryNotes,
    } = usePublisherActivitiesHook({ useMemoClient: noteMemo });

    const notesLoadingMessage = React.useMemo(() => {
        if (!isFetchingNotes || notesFetchAttempt === 0) return undefined;
        if (notesFetchAttempt === 1) return `Récupération des notes en cours…`;
        if (notesFetchAttempt === MAX_NOTES_FETCH_ATTEMPTS)
            return `Dernier essai en cours (${notesFetchAttempt}/${MAX_NOTES_FETCH_ATTEMPTS})…`;
        return `Nouvel essai en cours (${notesFetchAttempt}/${MAX_NOTES_FETCH_ATTEMPTS})…`;
    }, [isFetchingNotes, notesFetchAttempt]);

    const keycloakService = UseKeycloakService();
    const userIdentifier = keycloakService.getTokenParsed()?.preferred_username;
    const {
        orderedTasks,
        tasksLoading,
        handleAddTask,
        handleCheckTask,
        handleEditTask,
        handleUpdatePriority,
        handleDeleteTask,
    } = useTasksHook({
        apiTarget: 'tiers',
        linkedEntityId: publisher ? (publisher.id?.toString() as string) : undefined,
    });

    const handleLoadTechniques = React.useCallback(() => {
        setShowLogsTechniques((prev) => !prev);
    }, []);

    const textLogsTechnique = showLogsTechniques
        ? 'Masquer les actions techniques'
        : 'Afficher les actions techniques';

    const showIconTechniques = showLogsTechniques ? forbiddenIcon : preview;

    const actions = useMemo(
        () =>
            [
                showNotes &&
                    !notesFetchFailed && {
                        id: 'add-note',
                        label: 'Ajouter une note',
                        shortLabel: 'Ajouter',
                        icon: addNoteIcon,
                        onClick: handleAddNote,
                        variant: 'text' as const,
                        color: palette.grey[700],
                    },
                showTask && {
                    id: 'add-task',
                    label: 'Ajouter une tâche',
                    shortLabel: 'Ajouter',
                    icon: addTaskIcon,
                    onClick: handleAddTask,
                    variant: 'text' as const,
                    color: palette.grey[700],
                },
                showTechniqueLogs && {
                    id: 'show-technique',
                    label: textLogsTechnique,
                    shortLabel: 'Afficher',
                    icon: showIconTechniques,
                    onClick: handleLoadTechniques,
                    variant: 'text' as const,
                    color: palette.grey[700],
                },
            ].filter((action): action is Exclude<typeof action, false> => action !== false),
        [
            showNotes,
            notesFetchFailed,
            handleAddNote,
            palette.grey,
            showTask,
            handleAddTask,
            showTechniqueLogs,
            textLogsTechnique,
            showIconTechniques,
            handleLoadTechniques,
        ],
    );

    return (
        <ActivityHistoryCard actions={actions}>
            {showTask && publisher && (
                <TasksCardContent
                    tasks={orderedTasks as PrescripteurTachesApiObject[] | UtilisateurTaches[]}
                    tasksLoading={tasksLoading}
                    onCheckTask={handleCheckTask}
                    onEditTask={handleEditTask}
                    onDeleteTask={handleDeleteTask}
                    onPriorityChange={handleUpdatePriority}
                    userIdentifier={userIdentifier}
                />
            )}

            <ActivitiesCardContent
                activities={activities}
                onEditNote={handleEditNote}
                onDeleteNote={handleDeleteNote}
                activitiesLoading={activitiesLoading}
                notesLoadingMessage={notesLoadingMessage}
                notesError={
                    notesFetchFailed
                        ? 'Les notes sont temporairement indisponibles. Veuillez réessayer plus tard ou rafraîchir la page.'
                        : null
                }
                onRetryNotes={notesFetchFailed && !isFetchingNotes ? handleRetryNotes : undefined}
                isRetryingNotes={isFetchingNotes}
            />
        </ActivityHistoryCard>
    );
};

export default PublisherActivityTab;
